
(() => {
  if (customElements.get('dragon-tree-page')) return;
  class DragonTreePage extends HTMLElement {
    connectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.quantity = this.querySelector('[name="quantity"]');
      this.mainButton = this.querySelector('[data-main-submit]');
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.sticky = this.querySelector('[data-sticky]');
      this.status = this.querySelector('[data-status]');
      this.pending = false;
      this.moneyFormat = new Intl.NumberFormat(this.dataset.locale || 'en-CA', {
        style: 'currency', currency: this.dataset.currency || 'CAD', currencyDisplay: 'narrowSymbol'
      });
      this.form.addEventListener('change', () => this.update(), { signal });
      this.quantity.addEventListener('input', () => this.update(), { signal });
      this.querySelectorAll('[data-quantity-change]').forEach(button => {
        button.addEventListener('click', () => {
          const next = this.getQuantity() + Number(button.dataset.quantityChange);
          this.quantity.value = String(Math.min(10, Math.max(1, next)));
          this.update();
        }, { signal });
      });
      this.form.addEventListener('submit', event => this.addToCart(event), { signal });
      this.stickyButton?.addEventListener('click', () => this.form.requestSubmit(this.mainButton), { signal });
      this.querySelectorAll('[data-gallery-thumbnail]').forEach(link => {
        link.addEventListener('click', event => {
          const mainImage = this.querySelector('.db-media-slot img');
          if (!mainImage) return;
          event.preventDefault();
          mainImage.srcset = '';
          mainImage.src = link.href;
          mainImage.alt = link.querySelector('img')?.alt || 'Saiyan Tree';
        }, { signal });
      });
      if ('IntersectionObserver' in window && this.sticky) {
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(([entry]) => {
          const visible = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.sticky.hidden = !visible;
          document.body.classList.toggle('db-sticky-visible', visible);
        });
        this.observer.observe(this.mainButton);
      }
      this.reviewCarousel = this.querySelector('[data-review-carousel]');
      if (this.reviewCarousel) {
        const carousel = this.reviewCarousel;
        const cards = [...carousel.querySelectorAll('.db-review-card')];
        const controls = [...this.querySelectorAll('[data-review-direction]')];
        const updateReviewControls = () => {
          if (!cards.length) return;
          const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth;
          const index = Math.min(cards.length - 1, Math.max(0, Math.round(carousel.scrollLeft / Math.max(1, step))));
          this.querySelector('[data-review-position]').textContent = (index + 1) + ' / ' + cards.length;
          const end = Math.max(0, carousel.scrollWidth - carousel.clientWidth);
          controls.forEach(button => button.disabled = Number(button.dataset.reviewDirection) < 0 ? carousel.scrollLeft <= 2 : carousel.scrollLeft >= end - 2);
        };
        controls.forEach(button => {
          button.addEventListener('click', () => {
            const step = cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : carousel.clientWidth;
            carousel.scrollBy({ left: Number(button.dataset.reviewDirection) * step, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          }, { signal });
        });
        carousel.addEventListener('scroll', updateReviewControls, { signal, passive:true });
        carousel.addEventListener('keydown', event => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          controls.find(button => Number(button.dataset.reviewDirection) === direction)?.click();
        }, { signal });
        window.addEventListener('resize', updateReviewControls, { signal, passive:true });
        requestAnimationFrame(updateReviewControls);
      }
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      document.body.classList.remove('db-sticky-visible');
    }
    getQuantity() {
      return Math.min(10, Math.max(1, Math.floor(Number(this.quantity.value) || 1)));
    }
    getSelected() {
      return this.querySelector('input[name="id"]:checked');
    }
    getTotal() {
      const selected = this.getSelected();
      let total = Number(selected?.dataset.variantPrice || 0) * this.getQuantity();
      this.querySelectorAll('[data-addon-id]:checked').forEach(input => {
        total += Number(input.dataset.addonPrice) * (input.hasAttribute('data-single-addon') ? 1 : this.getQuantity());
      });
      return total;
    }
    update() {
      if (this.pending) return;
      const selected = this.getSelected();
      const available = Boolean(selected && selected.dataset.variantAvailable === 'true');
      const money = this.moneyFormat.format(this.getTotal() / 100);
      this.querySelectorAll('[data-total],[data-sticky-total]').forEach(el => el.textContent = money);
      const title = this.querySelector('[data-sticky-variant]');
      if (title) title.textContent = selected?.dataset.variantTitle || '';
      this.mainButton.disabled = !available;
      if (this.stickyButton) this.stickyButton.disabled = !available;
      this.querySelector('[data-button-label]').textContent = available ? 'Add to Cart' : 'Sold out';
      if (this.stickyButton) this.stickyButton.textContent = available ? 'Add to Cart' : 'Sold out';
      this.querySelector('[data-quantity-change="-1"]').disabled = this.getQuantity() <= 1;
      this.querySelector('[data-quantity-change="1"]').disabled = this.getQuantity() >= 10;
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.pending || !this.form.reportValidity()) return;
      const selected = this.getSelected();
      if (!selected || selected.dataset.variantAvailable !== 'true') return;
      this.quantity.value = String(this.getQuantity());
      const properties = {};
      this.form.querySelectorAll('input[name^="properties["]').forEach(input => {
        const key = input.name.slice(11, -1);
        properties[key] = input.value;
      });
      const items = [{ id: Number(selected.value), quantity: this.getQuantity(), properties }];
      this.querySelectorAll('[data-addon-id]:checked').forEach(input => {
        items.push({
          id: Number(input.dataset.addonId),
          quantity: input.hasAttribute('data-single-addon') ? 1 : this.getQuantity(),
          properties: { 'For tree': selected.dataset.variantTitle }
        });
      });
      this.pending = true;
      this.status.textContent = '';
      this.disabledStates = [...this.form.querySelectorAll('input,button')].map(el => [el, el.disabled]);
      this.disabledStates.forEach(([el]) => el.disabled = true);
      if (this.stickyButton) this.stickyButton.disabled = true;
      this.querySelector('[data-button-label]').textContent = 'Adding…';
      if (this.stickyButton) this.stickyButton.textContent = 'Adding…';
      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) {
          const description = result.description;
          throw new Error(typeof description === 'string' ? description : 'Please check availability and try again.');
        }
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.status.textContent = error instanceof TypeError
          ? 'We could not confirm your cart update. Check your cart before trying again. '
          : String(error.message || 'Your cart could not be updated. ');
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = ' View cart';
        link.style.textDecoration = 'underline';
        this.status.append(link);
        this.pending = false;
        this.disabledStates.forEach(([el, disabled]) => el.disabled = disabled);
        this.update();
      }
    }
  }
  customElements.define('dragon-tree-page', DragonTreePage);
})();
